import {FC, useCallback, useEffect, useState} from 'react';
import styled from 'styled-components';
import {useLocation, useParams} from 'wouter';

import {MIN_CYCLES_SINCE_LAST_CYCLES_WARNING} from '@shared/models/anomalies';
import {DoorMessageCategory, FrontendDoorMessage} from '@shared/models/api_models';

import {Button, ButtonAsLink} from '@shared_web/components/core/button';
import {showSuccess} from '@shared_web/components/core/notifications';
import {Flex} from '@shared_web/components/core2/flex';
import {notifyError} from '@shared_web/lib/notification';

import {AlertSymbol} from '@mavismart_frontend/components/alert_symbol';
import {TableWrapper} from '@mavismart_frontend/components/common_styles';
import {DateText} from '@mavismart_frontend/components/date_text';
import {Loader} from '@mavismart_frontend/components/loader';
import {Title} from '@mavismart_frontend/components/title';
import {findTranslationKey, translate, Translate} from '@mavismart_frontend/components/translate';
import {apiCall} from '@mavismart_frontend/lib/api';
import {BUTTON_THEME, categoryActions, LINK_THEME} from '@mavismart_frontend/lib/constants';
import {GenericCell} from '@mavismart_frontend/pages/cells/generic_cell';
import {SendToMaviflex} from '@mavismart_frontend/pages/tabs/send_to_maviflex';
import {getUserLanguage, useAccount} from '@mavismart_frontend/stores/account_store';
import {updateDoorStatus} from '@mavismart_frontend/stores/all_doors_store';

interface DoorDetailMessageProps {
  category: DoorMessageCategory;
  doorId: string;
}

export const DoorDetailMessage: FC<DoorDetailMessageProps> = ({category, doorId}) => {
  const {groupId} = useParams<{groupId?: string}>();
  const [location, navigate] = useLocation();
  const isSendPage = location.includes('/send');
  const [categoryMessages, setCategoryMessages] = useState<FrontendDoorMessage[] | undefined>();
  const account = useAccount();
  const [nextPaginationToken, setNextPaginationToken] = useState<string | undefined>(undefined);

  const listDoorMessages = useCallback(
    async (paginationToken?: string) => {
      try {
        const response = await apiCall('POST /list-door-messages', {
          id: doorId,
          category,
          limit: 5,
          paginationToken,
        });
        setCategoryMessages(prev => [...(prev ?? []), ...response.messages]);
        setNextPaginationToken(response.nextPaginationToken);
      } catch (error) {
        notifyError(error);
      }
    },
    [doorId, category]
  );

  useEffect(() => {
    listDoorMessages().catch(notifyError);
  }, [listDoorMessages]);

  const handleShowMore = useCallback(async () => {
    if (nextPaginationToken === undefined) {
      showSuccess(translate('no_others_messages', getUserLanguage(account)));
      return;
    }

    await listDoorMessages(nextPaginationToken);
  }, [nextPaginationToken, listDoorMessages, account]);

  // sort messages by most recent and get the most recent one
  const mostRecentMessage = categoryMessages?.sort((a, b) => b.ts - a.ts)[0];
  const otherMessages = categoryMessages?.filter(msg => msg !== mostRecentMessage) ?? [];

  const handleResolveAnomaly = useCallback(async () => {
    try {
      if (
        mostRecentMessage?.type !== 'anomaly' ||
        mostRecentMessage?.ts === undefined ||
        mostRecentMessage?.level !== 'yellow' ||
        mostRecentMessage.category !== 'door_cycles_warning'
      ) {
        throw new Error(translate('no_anomaly_to_resolve', getUserLanguage(account)));
      }
      const {newDoorStatus, newDoorMessages} = await apiCall(
        'POST /resolve-door-anomaly-category',
        {
          doorId,
          anomalyCategory: mostRecentMessage.category as 'door_cycles_warning',
        }
      );
      showSuccess(translate('anomaly_resolved_successfully', getUserLanguage(account)));
      setCategoryMessages(prev => [...(prev ?? []), ...newDoorMessages]);
      updateDoorStatus(doorId, newDoorStatus);
    } catch (error) {
      notifyError(error);
    }
  }, [mostRecentMessage, doorId, account]);

  const handleSend = useCallback(() => {
    groupId !== undefined
      ? navigate(`/groups/${groupId}/doors/${doorId}/messages/${category}/send`)
      : navigate(`/doors/${doorId}/messages/${category}/send`);
  }, [groupId, doorId, category, navigate]);

  if (!categoryMessages) {
    return <Loader />;
  }

  if (isSendPage && mostRecentMessage !== undefined) {
    return (
      <SendToMaviflex doorId={doorId} doorMessageTs={mostRecentMessage.ts} category={category} />
    );
  }
  const catTranslateKey = findTranslationKey(category);
  return (
    <Wrapper>
      <CustomMessageBlock>
        <StyledTitle
          name={
            catTranslateKey ? <Translate value={catTranslateKey} /> : mostRecentMessage?.category
          }
        />
        {mostRecentMessage?.ts && (
          <CustomData>
            <AlertSymbol level={mostRecentMessage?.level || 'green'} />
            <DateText date={new Date(mostRecentMessage.ts)} />
          </CustomData>
        )}
      </CustomMessageBlock>

      <SectionTitle>
        <Translate value="recommendations" />
      </SectionTitle>

      <SectionText>
        {catTranslateKey && categoryActions[catTranslateKey] ? (
          <Translate value={categoryActions[catTranslateKey]} params={{new_line: <br />}} />
        ) : (
          <Translate value="no_recommendations" />
        )}
        <br />
        {mostRecentMessage &&
          mostRecentMessage.category === 'door_cycles_warning' &&
          mostRecentMessage.doorCycles !== undefined && (
            <Translate
              value="door_cycles_warning_recommendation"
              params={{
                doorCycles: mostRecentMessage.doorCycles,
                threshold: MIN_CYCLES_SINCE_LAST_CYCLES_WARNING,
                new_line: <br />,
              }}
            />
          )}
      </SectionText>

      <SectionTitle>
        <Translate value="history_messages" />
      </SectionTitle>

      <TableWrapper>
        {otherMessages && otherMessages.length > 0 ? (
          otherMessages.map(msg => {
            const catKey = findTranslationKey(msg.category);
            return (
              <GenericCell
                key={msg.ts}
                id={msg.ts}
                icon={<AlertSymbol level={(msg.level as 'green' | 'yellow' | 'red') || 'green'} />}
                title={catKey !== undefined ? <Translate value={catKey} /> : msg.category}
                subtitle={<DateText date={msg.ts ? new Date(msg.ts) : new Date()} />}
                rightIcon={<Flex />}
              />
            );
          })
        ) : (
          <SectionText>
            <Translate value="no_others_messages" />
          </SectionText>
        )}
      </TableWrapper>
      {nextPaginationToken && (
        <WrapperLoadMore>
          <ButtonAsLink onClickAsync={handleShowMore} themeOverrides={LINK_THEME}>
            <Translate value="load_more" />
          </ButtonAsLink>
        </WrapperLoadMore>
      )}

      <WrapperButtons $justifyContent={'center'}>
        {mostRecentMessage?.type === 'anomaly' && (
          <Button onClickAsync={handleResolveAnomaly} themeOverrides={BUTTON_THEME}>
            <Translate value="resolve_anomaly" />
          </Button>
        )}
        <Button onClick={handleSend} themeOverrides={BUTTON_THEME}>
          <Translate value="send_to_Maviflex" />
        </Button>
      </WrapperButtons>
    </Wrapper>
  );
};

DoorDetailMessage.displayName = 'DoorDetailMessage';

const Wrapper = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
  height: 100%;
`;

const WrapperButtons = styled.div<{$justifyContent: 'center' | 'space-between'}>`
  position: sticky;
  bottom: 0;
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  gap: 10px;
  align-items: center;
  justify-content: ${props => props.$justifyContent};
  /* justify-content: center; */
  background: white;
  z-index: 1;
`;

export const WrapperLoadMore = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 10px;
`;

const CustomMessageBlock = styled.div`
  display: flex;
  flex-direction: column;

`;

const SectionTitle = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #232a36;
  margin-top: 16px;
`;

const SectionText = styled.div`
  font-size: 1rem;
  color: #4a4a4a;
  margin-bottom: 18px;
  padding: 0 10px;
`;

const CustomData = styled.div`
  display: flex;
  align-items: center;
  margin-top: 6px;
  gap: 10px;
  justify-content: center;
`;

// const WrapperButtons2 = styled.div`
//     display: flex;
//     gap: 20px;
//     justify-content: center;
//   `;

const StyledTitle = styled(Title)`
  margin-bottom: 8px;
`;
